import { useState, useCallback } from 'react';
import { useSnackbarContext } from '../../context/SnackbarContext';

/**
 * Show a toast via the app-wide SnackbarProvider (context/SnackbarContext.jsx).
 * Outside the provider (isolated component tests) it falls back to local state,
 * exposed as `snackbar` for assertions.
 */
export function useSnackbar() {
  const context = useSnackbarContext();

  const [localSnackbar, setLocalSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 6000,
  });

  const showSnackbar = useCallback((message, severity = 'info', autoHideDuration = 6000) => {
    if (context) {
      context.showSnackbar(message, severity, autoHideDuration);
      return;
    }
    setLocalSnackbar({ open: true, message, severity, autoHideDuration });
  }, [context]);

  const showSuccess = useCallback((message, autoHideDuration = 6000) => {
    showSnackbar(message, 'success', autoHideDuration);
  }, [showSnackbar]);

  const showError = useCallback((message, autoHideDuration = 6000) => {
    showSnackbar(message, 'error', autoHideDuration);
  }, [showSnackbar]);

  const showWarning = useCallback((message, autoHideDuration = 6000) => {
    showSnackbar(message, 'warning', autoHideDuration);
  }, [showSnackbar]);

  const showInfo = useCallback((message, autoHideDuration = 6000) => {
    showSnackbar(message, 'info', autoHideDuration);
  }, [showSnackbar]);

  const hideSnackbar = useCallback(() => {
    if (context) {
      context.hideSnackbar();
      return;
    }
    setLocalSnackbar(prev => ({ ...prev, open: false }));
  }, [context]);

  return {
    snackbar: localSnackbar,
    showSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideSnackbar,
  };
}
